import { Injectable } from '@angular/core';
import { Headers, Response } from '@angular/http';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';

import { AwsHttp } from '../../aws/http';
import { PublisherService } from '../../publisher/publisher.service';
import { ErrorHandler } from '../../restapi/error-handler';
import { UserService } from '../../user/user.service';
import { TargetValidationDialog, TargetValidationDialogArgs } from './target-validation-dialog.component';
import { TargetValidationSubscription } from './target-validation-subscription';

class Subscription {
    ID: string;
    Name: string;
    UserName: string;
    Valid: boolean;
}

@Injectable()
export class TargetValidationService {

    private static readonly URL_PARAM = 'targetValidation';
    private static readonly URL_PARAM_OK = 'ok';
    private static readonly URL_PARAM_ERROR = 'error';

    private dialog: TargetValidationDialog;

    constructor(
        private router: Router,
        private http: AwsHttp,
        private errorHandler: ErrorHandler,
        private userService: UserService,
        private publisherService: PublisherService
    ) {

        router.events
            .filter(event => event instanceof NavigationEnd)
            .map(event => event as NavigationEnd)
            .subscribe(event => {
                let url = this.createUrl(window.location.origin + event.url);
                let searchParams = this.getSearchParams(url.search);
                let targetValidation = this.getSearchParam(searchParams, TargetValidationService.URL_PARAM);
                let lastAuthzOk = this.getSearchParam(searchParams, TargetValidationService.URL_PARAM_OK);
                let lastAuthzErrorCode = this.getSearchParam(searchParams, TargetValidationService.URL_PARAM_ERROR);
                if (targetValidation && targetValidation.toLowerCase() === 'true') {
                    this.showDialog2(!!lastAuthzOk, lastAuthzErrorCode);
                }
            });
    }

    private get baseUrl() {
        return `${this.userService.getApiUrl()}/subscriptions`;
    }

    private getSearchParams(search: string) {
        let queryString = (search || '').substring(1);
        let searchParams = {};
        queryString.split('&').forEach(function (paramString) {
            let parts = paramString.split('=');

            let param = decodeURIComponent(parts[0]);
            if (param.length > 0) {
                let values = searchParams[param] = searchParams[param] || [];

                var value = parts[1];
                if (value !== undefined) {
                    value = decodeURIComponent(value);
                }
                values.push(value);
            }
        });
        return searchParams;
    }

    private getSearchParam(searchParams: any, param: string) {
        let values = searchParams[param];
        return values && values[0];
    }

    private createSearchString(searchParams: any) {
        searchParams = searchParams || {};
        let queryString = Object.keys(searchParams)
            .filter(param => param.length > 0)
            .reduce((paramStrings, param) => paramStrings.concat(searchParams[param].map((value: string) => value === undefined ? param : `${param}=${value}`)), [])
            .join('&');
        return queryString.length > 0 ? ('?' + queryString) : '';
    }

    private createUrl(url: string, base?: string) {
        let iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
        let iframeDocument = iframe.contentWindow.document;

        if (base) {
            let baseElement = iframeDocument.createElement('base');
            baseElement.href = base;
            iframeDocument.head.appendChild(baseElement);
        }

        var aElement = iframeDocument.createElement('a');
        aElement.href = url;
        aElement = iframeDocument.body.appendChild(aElement);

        document.body.removeChild(iframe);
        return aElement;
    }

    private fromSubscription(subscription: Subscription): TargetValidationSubscription {
        return {
            id: subscription.ID,
            name: subscription.Name,
            user: subscription.UserName,
            valid: subscription.Valid
        };
    }

    public getCleanUrl() {
        let url = this.createUrl(window.location.href);
        let searchParams = this.getSearchParams(url.search);
        delete searchParams[TargetValidationService.URL_PARAM];
        delete searchParams[TargetValidationService.URL_PARAM_OK];
        delete searchParams[TargetValidationService.URL_PARAM_ERROR];
        let search = this.createSearchString(searchParams);
        return `${url.pathname}${search}${url.hash}`;
    }

    public registerDialog(dialog: TargetValidationDialog) {
        this.dialog = dialog;
    }

    public showDialog() {
        this.showDialog2(true);
    }

    public showDialog2(lastAuthzOk: boolean, lastAuthzErrorCode?: string) {
        setTimeout(() => {
            let args = new TargetValidationDialogArgs();
            args.LastAuthzOK = lastAuthzOk;
            args.LastAuthzErrorCode = lastAuthzErrorCode
            this.dialog.showDialog(args);
        });
    }

    public getSubscriptions(): Observable<TargetValidationSubscription[]> {
        let headers = new Headers({
            'Accept': 'application/json'
        });
        return this.http
            .get(this.baseUrl, { headers: headers })
            .map((response: Response) => response.json().map(this.fromSubscription))
            .catch((response: Response) => this.errorHandler.throwError(response));
    }

    public addSubscription(subscriptionId: string): Observable<TargetValidationSubscription> {
        let headers = new Headers({
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        });
        return this.http
            .post(this.baseUrl, JSON.stringify({
                ID: subscriptionId.trim()
            }), { headers: headers })
            .map((response: Response) => this.fromSubscription(response.json()))
            .catch((response: Response) => this.errorHandler.throwError(response));
    }

    public removeSubscription(subscription: TargetValidationSubscription): Observable<TargetValidationSubscription> {
        let headers = new Headers({
            'Accept': 'application/json'
        });
        let url = `${this.baseUrl}/${subscription.id}`;
        return this.http
            .delete(url, { headers: headers })
            .map(() => subscription)
            .catch((response: Response) => this.errorHandler.throwError(response));
    }

    public authorizeSubscription(
        subscription: TargetValidationSubscription,
        returnUrl: string,
        adminConsent: boolean): Observable<string> {
        let headers = new Headers({
            'Accept': 'application/json'
        });
        let qparams = '';

        if (adminConsent) {
            qparams = '?admin=true';
        }

        let url = `${this.baseUrl}/${subscription.id}/auth${qparams}`;
        return this.http
            .get(url, {
                headers: headers
            }).map((response: Response) => response.json().authorization_uri)
            .catch((response: Response) => this.errorHandler.throwError(response));
    }

}


// WEBPACK FOOTER //
// ./src/app/main/target-validation/target-validation.service.ts